import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { InviteRotation } from "@/types/sms";

const INVITE_ROTATION_QUERY_KEY = ["sms-invite-rotation"];

/**
 * Reads the signed-in store's one sms_invite_rotations row. RLS scopes the
 * select to the caller's own store_id (sms_apply_store_rls), so a plain
 * select().single() is enough — the play-engine migration seeds exactly one
 * row per store. Not in the generated Supabase Database types yet, so this
 * goes through an untyped client (same pattern as useMarketingSettings).
 */
export const useInviteRotation = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<InviteRotation>({
    queryKey: INVITE_ROTATION_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb.from("sms_invite_rotations").select("*").single();
      if (error) throw error;
      return data as InviteRotation;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/** Updates the invite rotation row and invalidates the query on success. */
export const useUpdateInviteRotation = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<InviteRotation> & { store_id: string }): Promise<InviteRotation> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("sms_invite_rotations")
        .update(updates)
        .eq("store_id", updates.store_id)
        .select()
        .single();
      if (error) throw error;
      return data as InviteRotation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITE_ROTATION_QUERY_KEY });
    },
  });
};
